import { useQuery, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query'
import { api } from 'services'
import { rolesMatrixQueryKeys, assertOk } from './useRolesMatrix'

// ── Types ────────────────────────────────────────────────────────────────────

export interface CompanyRoleListItem {
  id: string
  name: string
  isCustom: boolean
  description?: string | null
  duplicated_from?: string | null
  created_at?: string
  updated_at?: string | null
  /** True when this built-in role has a company-level permissions override. */
  isOverridden?: boolean
  /**
   * The global/built-in role id — set for built-in roles (both overridden and
   * unmodified). Undefined for standalone custom roles.
   */
  builtinRoleId?: string
  /** Number of users assigned to this role within the company. */
  memberCount?: number
  /** Members assigned to this role — for the stacked-avatar Users column. */
  members?: RoleMemberSummary[]
}

/** A role member as returned in the roles list (matches backend IRoleMember). */
export interface RoleMemberSummary {
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
}

// POST /roles/company/custom body
interface CreateCompanyRolePayload {
  name: string
  description?: string
  permissionIds: string[]
}

// PATCH /roles/company/custom/:id body
interface RenameCompanyRolePayload {
  id: string
  name: string
  description?: string
}

// POST /roles/company/custom/:id/duplicate body
interface DuplicateCompanyRolePayload {
  /** id is unused by the API for routing (see controller note) but kept for invalidation. */
  id: string
  newName: string
  /** Built-in role name (e.g. 'company_admin') or existing custom role name. */
  sourceRoleName: string
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const companyRoleQueryKeys = {
  all: ['roles', 'company'] as const,
  lists: () => companyRoleQueryKeys.all,
}

// ── Query hook ───────────────────────────────────────────────────────────────

export const useCompanyRoles = () => {
  return useQuery<CompanyRoleListItem[]>({
    queryKey: companyRoleQueryKeys.lists(),
    queryFn: async () => {
      const response = await api.get('/roles/company')
      // The endpoint is company-scoped; guarantee an array so the table never
      // receives a non-array body (e.g. a "No company access" error shape for
      // users without a company context).
      const body = response.data
      return Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : []
    },
    staleTime: 1000 * 60 * 2,
  })
}

// ── Mutation hooks ───────────────────────────────────────────────────────────

export const useCreateCompanyRole = (
  options?: UseMutationOptions<CompanyRoleListItem, Error, CreateCompanyRolePayload>
) => {
  const queryClient = useQueryClient()
  return useMutation<CompanyRoleListItem, Error, CreateCompanyRolePayload>({
    ...options,
    mutationFn: (payload) => api.post('/roles/company/custom', payload).then(r => r.data),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: companyRoleQueryKeys.lists() })
      // The role detail page reads from the matrix; invalidate it too so an
      // immediate navigate-to-new-role finds the freshly-created role (same
      // reasoning as the duplicate hook below).
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
      options?.onSuccess?.(...args)
    },
  })
}

export const useRenameCompanyRole = (
  options?: UseMutationOptions<CompanyRoleListItem, Error, RenameCompanyRolePayload>
) => {
  const queryClient = useQueryClient()
  return useMutation<CompanyRoleListItem, Error, RenameCompanyRolePayload>({
    ...options,
    mutationFn: ({ id, name, description }) =>
      api.patch(`/roles/company/custom/${id}`, { name, description }).then(r => r.data),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: companyRoleQueryKeys.lists() })
      options?.onSuccess?.(...args)
    },
  })
}

export const useDuplicateCompanyRole = (
  options?: UseMutationOptions<CompanyRoleListItem, Error, DuplicateCompanyRolePayload>
) => {
  const queryClient = useQueryClient()
  return useMutation<CompanyRoleListItem, Error, DuplicateCompanyRolePayload>({
    ...options,
    mutationFn: ({ id, newName, sourceRoleName }) =>
      api.post(`/roles/company/custom/${id}/duplicate`, { newName, sourceRoleName }).then(assertOk).then(r => r.data),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: companyRoleQueryKeys.lists() })
      // The role detail page reads from the matrix; invalidate it too so a
      // navigate-to-new-duplicate finds the freshly-created role.
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
      options?.onSuccess?.(...args)
    },
  })
}

export const useDeleteCompanyRole = (
  options?: UseMutationOptions<void, Error, string>
) => {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    ...options,
    mutationFn: (id) => api.delete(`/roles/company/custom/${id}`).then(() => undefined),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: companyRoleQueryKeys.lists() })
      options?.onSuccess?.(...args)
    },
  })
}
